import { useQuery } from "@tanstack/react-query";
import { Card, Drawer, Typography } from "antd";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { apiFetch, withQuery } from "../api/client";
import type { ListResponse, SpeakerRow, SpeechRow } from "../api/types";
import { DataTable } from "../components/DataTable";
import { FilterBar } from "../components/FilterBar";
import type { FilterValues } from "../components/FilterBar";
import { useSpeakerColumns, useSpeechColumns } from "../components/domainColumns";

/**
 * Sidebar entry "speech_view" (navigation.speech_view) → /data/speech_data.
 *
 * GET /data/speeches lists the active speeches, GET /data/speeches/:id/speakers
 * the speakers who offer one — the drill-down is what the screen exists for, so
 * a coordinator can see who could hold a given talk.
 */
export function SpeechDataPage() {
  const { t } = useTranslation();
  const [filters, setFilters] = useState<FilterValues>({});
  const [selected, setSelected] = useState<SpeechRow | null>(null);

  const speechColumns = useSpeechColumns();
  const speakerColumns = useSpeakerColumns();

  const speeches = useQuery({
    queryKey: ["data", "speeches", filters],
    queryFn: () => apiFetch<ListResponse<SpeechRow>>(withQuery("/data/speeches", filters)),
  });

  const speakers = useQuery({
    queryKey: ["data", "speech-speakers", selected?.id],
    queryFn: () =>
      apiFetch<ListResponse<SpeakerRow>>(`/data/speeches/${selected!.id}/speakers`),
    enabled: selected !== null,
  });

  const fields = useMemo(
    () => [
      { name: "schema", label: t("activerecord.attributes.speech.schema") },
      { name: "title", label: t("activerecord.attributes.speech.title") },
      {
        name: "language_code",
        label: t("activerecord.attributes.speech.language_id"),
      },
    ],
    [t],
  );

  return (
    <Card title={t("navigation.speech_view")}>
      <FilterBar fields={fields} onApply={setFilters} loading={speeches.isFetching} />

      <DataTable
        columns={speechColumns}
        rows={speeches.data?.data}
        exportAs={t("navigation.speech_view")}
        loading={speeches.isPending}
        error={speeches.error}
        onRowClick={setSelected}
      />

      <Drawer
        open={selected !== null}
        onClose={() => setSelected(null)}
        size={960}
        destroyOnHidden
        title={
          selected && (
            <Typography.Text>
              {[selected.schema, selected.title].filter(Boolean).join(" – ")}
            </Typography.Text>
          )
        }
      >
        <Typography.Title level={5}>{t("speech.speakers")}</Typography.Title>

        {/* Names the talk, not just "speakers": the export lands in a folder
            where nothing else says which of them it belongs to. */}
        <DataTable
          columns={speakerColumns}
          rows={speakers.data?.data}
          exportAs={[selected?.schema, t("speech.speakers")].filter(Boolean).join(" · ")}
          loading={speakers.isPending}
          error={speakers.error}
          paginated={false}
        />
      </Drawer>
    </Card>
  );
}
